import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from './contexts/AuthContext'
import { useAuth } from './contexts/useAuth'
import ProtectedRoute from './components/ProtectedRoute'
import NavBar from './components/NavBar'
import SystemBanner from './components/SystemBanner'
import Spinner from './components/Spinner'
import NotFound from './pages/NotFound'
import { chunks } from './routeChunks'
import { prefetchOnIdle } from './utils/prefetch'
import './App.css'

// Every page is its own chunk. Before this, one bundle carried all of them
// plus maplibre-gl and recharts, so a viewer who only ever opened the alerts
// table still downloaded and parsed the admin tooling, the map engine and the
// charting library.
//
// NotFound is deliberately not among them: ProtectedRoute imports it
// statically for its forbidden case, so splitting it here would only produce
// a dynamic import of a module already sitting in the main chunk.
const Login              = lazy(chunks.login)
const Register           = lazy(chunks.register)
const Instruments        = lazy(chunks.instruments)
const InstrumentDetail   = lazy(chunks.instrumentDetail)
const InstrumentMetrics  = lazy(chunks.instrumentMetrics)
const CalculatedMetrics  = lazy(chunks.calculatedMetrics)
const DeviceSetup        = lazy(chunks.deviceSetup)
const AdminDashboard     = lazy(chunks.adminDashboard)
const AdminAccountDetail = lazy(chunks.adminAccountDetail)
const AdminUserDetail    = lazy(chunks.adminUserDetail)
const AdminFleetOverview = lazy(chunks.adminFleetOverview)
const AdminReingest      = lazy(chunks.adminReingest)
const AdminBanner        = lazy(chunks.adminBanner)
const Help               = lazy(chunks.help)
const Settings           = lazy(chunks.settings)

// Pages that paint their own full-bleed background. .main-content is a
// centred 1200px box with 2rem padding, so anything relying on a background
// reaching the viewport edge -- the auth pages' brand gradient -- has to opt
// out of that box rather than fight it with negative margins, which would
// reintroduce horizontal scroll.
const FULL_BLEED_ROUTES = ['/login', '/register']

/**
 * What to warm once the first screen has painted, chosen by who is looking.
 *
 * Deliberately short. Prefetching everything would just rebuild the old
 * single bundle over a slower path; these are the one or two screens a
 * session actually reaches next, heaviest first so it gets the most time.
 */
function prefetchTargets({ isAuthenticated, isSuperAdmin }) {
  if (!isAuthenticated) {
    // On the auth pages the only onward navigation is the other auth page.
    return [['login', chunks.login], ['register', chunks.register]]
  }
  if (isSuperAdmin) {
    return [
      ['adminAccountDetail', chunks.adminAccountDetail],
      ['adminFleetOverview', chunks.adminFleetOverview],
      ['usageChartModal', chunks.usageChartModal],
      ['settings', chunks.settings],
    ]
  }
  // The instrument list is the landing page and its drill-down is the next
  // click in almost every session. instrumentDetail is also the largest page
  // in the app and pulls the shared recharts chunk in with it, so warming it
  // covers the charts routes too.
  return [
    ['instrumentDetail', chunks.instrumentDetail],
    ['instrumentMap', chunks.instrumentMap],
    ['settings', chunks.settings],
    ['help', chunks.help],
  ]
}

function AppContent() {
  const { user, isAuthenticated, isLoading } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const fullBleed = FULL_BLEED_ROUTES.includes(pathname)

  // Waits for the auth probe: which chunks are worth warming depends on the
  // role, and guessing before /me resolves would warm the admin pages for
  // every viewer.
  useEffect(() => {
    if (isLoading) return undefined
    return prefetchOnIdle(prefetchTargets({ isAuthenticated, isSuperAdmin }))
  }, [isLoading, isAuthenticated, isSuperAdmin])

  return (
    <div className="app">
      <NavBar />
      {isAuthenticated && <SystemBanner />}
      <main className={fullBleed ? 'main-content main-content--full' : 'main-content'}>
        {/* Scoped to the routes rather than the shell, so the navbar and the
            system banner render immediately and stay put while a page chunk
            lands, instead of the whole frame blinking out. */}
        <Suspense fallback={<Spinner center size="lg" label={t('common.loading')} />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  {isSuperAdmin ? <AdminDashboard /> : <Instruments />}
                </ProtectedRoute>
              }
            />
            <Route
              path="/instruments"
              element={
                <ProtectedRoute>
                  <Instruments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instruments/:id"
              element={
                <ProtectedRoute>
                  <InstrumentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instruments/:id/metrics"
              element={
                <ProtectedRoute>
                  <InstrumentMetrics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instruments/:id/calculated-metrics"
              element={
                <ProtectedRoute>
                  <CalculatedMetrics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instruments/:id/setup"
              element={
                <ProtectedRoute>
                  <DeviceSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fleet"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminFleetOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reingest"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminReingest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/banner"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminBanner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/accounts/:accountId"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminAccountDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/accounts/:accountId/users/:userId"
              element={
                <ProtectedRoute requireRole="super_admin">
                  <AdminUserDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            {/* Anything unmatched. Deliberately last. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  )
}

export default App
